import './shipment.scss';

import { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ApplicationState } from '../../../../store';
import { IOrder } from '../../../../store/ducks/order/types';
import Note from '../note/Note';
import Address from './address/Address';
import Identification from './client/Identification';
import CloseOrder from './payment/CloseOrder';
import Payment from './payment/Payment';

interface IShipmentContext {
  step: number;
  setStep: (step: 1 | 2 | 3 | 4) => void;
}

const ShipmentContext = createContext<IShipmentContext | null>(null);

export const useShipmentContext = (): IShipmentContext => {
  const context = useContext(ShipmentContext);

  if (!context)
    throw new Error('Esse compomente deve ser usado dentro de um <Shipment> componente.');

  return context;
};

interface Props {
  order: IOrder;
  handleFinishOrder: () => boolean;
  forwardedOrderAlert: (alert: boolean) => void;
  setShipmentStep: (step: 1 | 2 | 3 | 4) => void;
  showPayment?: boolean;
}

function Shipment({
  setShipmentStep,
  handleFinishOrder,
  forwardedOrderAlert,
  showPayment = false,
}: Props) {
  const selectOrder = useSelector<ApplicationState, IOrder | null>((state) => state.order.data);

  const calculateStep = (): 1 | 2 | 3 | 4 => {
    if (!selectOrder) return 1;

    if (!selectOrder.client || selectOrder.client.type === 3) return 1;

    if (!selectOrder.shipment?.pickup_at) return 2;

    const validPayments = selectOrder.payments
      ?.filter((p) => !p.deleted_at)
      .filter((p, index, arr) => {
        const next = arr[index + 1];
        const prev = arr[index - 1];
        const isSameAsNext = next && p.total_paid === next.total_paid;
        const prevSumExceeds = prev && p.total_paid + prev.total_paid > selectOrder.total;
        return !(isSameAsNext && prevSumExceeds);
      });

    const hasPaymentSeted = validPayments?.some((p) => p.id && p.payment_plan_id);
    const hasBlockedMedicine = selectOrder.cart.products.some(
      (item) => item.prescription_retention,
    );
    const hasDeliverySeted = selectOrder.shipment?.pickup_at || selectOrder.shipment?.address;

    if (hasPaymentSeted || (hasBlockedMedicine && hasDeliverySeted)) return 4;

    return 3;
  };

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  useEffect(() => {
    const calculatedStep = calculateStep();
    setStep(calculatedStep);
  }, [selectOrder?.client, selectOrder?.shipment, selectOrder?.payments]);

  useEffect(() => setShipmentStep(step), [step, setShipmentStep]);

  return (
    <>
      {selectOrder && (
        <ShipmentContext.Provider value={{ step, setStep }}>
          <div className="shipment mb-2 mt-3 mt-sm-0">
            {!showPayment && (
              <>
                <Identification client={selectOrder.client} orderId={selectOrder.id} />
                <Address order={selectOrder} />
              </>
            )}

            {showPayment && (
              <Payment
                order={selectOrder}
                handleFinishOrder={handleFinishOrder}
                forwardedOrderAlert={forwardedOrderAlert}
              />
            )}
          </div>
          {showPayment && (
            <>
              <Note note={selectOrder.note} orderId={selectOrder.id} status={selectOrder.status} />
              <CloseOrder
                order={selectOrder}
                handleFinishOrder={handleFinishOrder}
                forwardedOrderAlert={forwardedOrderAlert}
              />
            </>
          )}
        </ShipmentContext.Provider>
      )}
    </>
  );
}

export default Shipment;
